import { useState } from 'react';
import { useTranslation } from '@jupiter/plugin-runtime/i18n';
import { CODE_SUCCESS } from '@features/constants/network';
import {
  Contents,
  GetContentsTreeRequest,
  LearningInfo,
  ListContentsRequest,
  sellerLearningCenterApiClient as apiClient,
} from '@/api/seller/serv/oec_seller_learning_center_api';
import { RoleType } from '@/constants';

export const useContents = () => {
  const { i18n } = useTranslation();
  const [menu, setMenu] = useState({
    [RoleType.Seller]: [] as Contents[],
    [RoleType.Creator]: [] as Contents[],
  });
  const [list, setList] = useState<ReturnType<typeof findLeafs>>({});
  const findLeafs = (nodes: Contents[], findLevel?: number) => {
    const leafs: {
      [id: number]: {
        id: number;
        name: string;
        path: number[];
        total: number;
        _handler: {
          [langPage: string]: ReturnType<(typeof actions)['loadPage']>;
        };
        bucket: {
          [langPage: string]: LearningInfo[];
        };
      };
    } = {};
    const find = (nodes: Contents[], path: number[] = []) => {
      for (let i = 0, l = nodes.length; i < l; i++) {
        const { content_id, content_name = '', sub_contents = [] } = nodes[i];
        const newPath = [...path, content_id];

        if (
          (typeof findLevel === 'number' && newPath.length >= findLevel) ||
          !sub_contents.length
        ) {
          const id = content_id;
          if (!leafs[id]) {
            leafs[id] = {
              id: content_id,
              name: content_name,
              path: [],
              total: 0,
              _handler: {},
              bucket: {},
            };
          }
          leafs[id].path = newPath;
        } else {
          find(nodes[i].sub_contents || [], newPath);
        }
      }
    };

    if (findLevel !== 0) {
      find(nodes);
    }

    return leafs;
  };

  const state = {
    menu,
    list,
  };
  const actions = {
    init: async (request: GetContentsTreeRequest, findLevel?: number) => {
      const { role_type: role } = request;

      const res = await apiClient.GetContentsTree(request);

      if (res?.code === CODE_SUCCESS) {
        const newMenu = res.data?.contents?.sub_contents || [];

        if (role) {
          setMenu(oldMenu => ({
            ...oldMenu,
            [role]: newMenu,
          }));
        }
        setList(oldList => {
          const newList = {
            ...oldList,
            ...findLeafs(newMenu, findLevel),
          };

          if (res.data?.content_id_to_info) {
            const infos = res.data?.content_id_to_info || {};
            const curPage = 1;
            Object.keys(infos).forEach(id => {
              const info = infos[id];
              const item = newList[+id];

              if (item) {
                item.total = info.total || info.learning_info?.length || 0;
                item.bucket[`${i18n.language}_${curPage}`] =
                  info.learning_info || [];
              }
            });
          }

          return newList;
        });
      }

      return res;
    },
    loadPage: async (
      request: {
        content_id: number;
        page: number;
      } & Omit<ListContentsRequest, 'content_id' | 'page'>,
    ) => {
      const { content_id: contentId, page } = request;
      const pageStr = `${i18n.language}_${page}`;
      const handler = list[contentId]?._handler;

      if (!handler[pageStr]) {
        handler[pageStr] = new Promise(resolve => {
          apiClient.ListContents(request).then(res => {
            handler[pageStr] = null;

            if (
              res?.code === CODE_SUCCESS &&
              contentId !== undefined &&
              page !== undefined
            ) {
              setList(oldList => {
                const infos = res.data?.learning_info || [];
                const total = res.data?.total;
                const repo = (oldList[contentId] = oldList[contentId] || {});

                repo.total = total || repo.total || 0;
                repo.bucket[pageStr] = infos;

                return {
                  ...oldList,
                };
              });
            }

            resolve(res);
          });
        });
      }

      return handler[pageStr];
    },
    getPathFromContentsById: (contentId: number, contents: Contents[] = []) => {
      const paths: number[] = [];
      const find = (id: number, nodes: typeof contents) => {
        for (let i = 0, l = nodes.length; i < l; i++) {
          const n = nodes[i];
          const cId = n.content_id;

          if (cId === id || find(id, n.sub_contents || [])) {
            paths.unshift(cId);

            if (cId === id && n.sub_contents?.length) {
              const fPath = actions.getPathFromFirst(n.sub_contents);

              paths.push(...fPath);
            }

            return true;
          }
        }

        return false;
      };
      find(contentId, contents);
      return paths;
    },
    getPathFromFirst: (contents: Contents[] = []): number[] => {
      const node = contents[0];

      if (node) {
        return [
          node.content_id,
          ...actions.getPathFromFirst(node.sub_contents),
        ];
      }

      return [];
    },
  };

  return {
    state,
    actions,
  };
};
